import { useEffect, useRef, useState } from "react";

import { WebSocketReponse } from "fieldpro-tools";
import { defaultLogger } from "log/FieldProLogger";
import { useSelector } from "react-redux";

import apiService from "api/apiService";
import { API_BASE_URL } from "api/endpoints";
import { getSelectedClient } from "containers/clients/redux/selectors";

const useWebSocket = <T = any>(
  callBackHandler: (event: WebSocketReponse<T>) => void,
  dependencies: React.DependencyList = [],
  maxRetries: number = 5,
  retryDelay: number = 1000,
) => {
  const isLocalBackend = window._env_?.NODE_ENV === "local";
  const protocol = isLocalBackend ? "ws" : "wss";
  const client = useSelector(getSelectedClient);

  const url = API_BASE_URL({
    customPath: isLocalBackend ? "/ws" : "/web/ws",
    includePath: false,
  }).replace(/https?/, protocol);

  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const retryCountRef = useRef<number>(0);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef<boolean>(true);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const connect = () => {
    if (!shouldReconnectRef.current) return;

    const wsUrl = new URL(url);
    if (client?.id) {
      wsUrl.searchParams.set("client_id", client.id);
    }

    wsUrl.searchParams.set("token", apiService.token);

    const ws = new WebSocket(wsUrl.toString());
    setSocket(ws);

    ws.onopen = (e) => {
      setIsConnected(true);
      retryCountRef.current = 0;

      // Start ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 10000);
    };

    ws.onmessage = (event) => {
      callBackHandler(JSON.parse(event.data));
    };

    ws.onclose = (e) => {
      setIsConnected(false);
      setSocket(null);

      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }

      if (shouldReconnectRef.current && retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        const delay = retryDelay * Math.pow(2, retryCountRef.current - 1);

        retryTimeoutRef.current = setTimeout(() => {
          connect();
        }, delay);
      }
    };

    ws.onerror = (e) => {
      console.error("WebSocket error:", e);
      setIsConnected(false);
    };
  };

  useEffect(() => {
    shouldReconnectRef.current = true;
    retryCountRef.current = 0;

    try {
      connect();
    } catch (error) {
      defaultLogger.logError(error);
      setIsConnected(false);
    }

    return () => {
      shouldReconnectRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
      if (socket) {
        socket.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { socket, isConnected };
};

export default useWebSocket;
